import { useTranslation } from '@/hooks/useTransition';
import { Close } from '../../../assets/close';
import { CoinInfoDisplay } from './coin-status';
import { styles as baseStyles } from './index';
import { css } from '@emotion/react';
import { Trans } from '@/components/i18n';
import { defaultCoinImage } from '@/assets/coin';
import { FixedImage } from './fixed-image';
import { TipsBubble } from '@/assets/tips';

interface CoinUnloginContentProps {
  rewardType: 'JOLI_COIN' | 'ADS-JOLI_COIN' | 'JOLI_GEM' | 'ADS-JOLI_GEM';
  currency: 'JOLI_COIN' | 'JOLI_GEM';
  rewardValue: number;
  confirmButtonKey: string;
  cancelButtonKey: string;
  onClose: (params: { type?: 'CANCEL' | 'FAILED' | 'CONFIRM' }) => void;
  onConfirm: () => void;
}

const styles = {
  ...baseStyles,
  header: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
  `,
  introduct: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 24px;
    gap: 8px;
    padding: 0 20px;
  `,
  introductSubTitle: css`
    font-size: 14px;
    color: rgb(255, 255, 255);
    opacity: 0.5;
    line-height: 21px;
    text-align: center;
    font-weight: 600;
  `,
  introductTitle: css`
    font-size: 16px;
    color: #ffffff;
    line-height: 24px;
    text-align: center;
    font-weight: 600;
  `,
  tipsBubble: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #4f396d;
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    border-radius: 20px;
    padding: 0 16px;
    height: 32px;
    position: relative;
    box-sizing: border-box;
    white-space: nowrap;
    text-overflow: ellipsis;
    border: 1px solid #ffffff52;
    z-index: 1;

    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: -8px;
      transform: translateX(-50%);
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 8px solid #4f396d;
      z-index: 2;
    }
  `
};

const CoinImageMap = {
  JOLI_COIN: 'https://fe-oss.jolibox.com/static/rewards/jolicoin-light.png?timestamp=' + Date.now(),
  'ADS-JOLI_COIN': 'https://fe-oss.jolibox.com/static/rewards/ads-jolicoin-light.png?timestamp=' + Date.now(),
  JOLI_GEM: 'https://fe-oss.jolibox.com/static/rewards/jolicoin-light.png?timestamp=' + Date.now(),
  'ADS-JOLI_GEM': 'https://fe-oss.jolibox.com/static/rewards/ads-jolicoin-light.png?timestamp=' + Date.now()
};

export const CoinUnloginContent = ({
  currency,
  rewardType,
  rewardValue,
  confirmButtonKey,
  cancelButtonKey,
  onClose,
  onConfirm
}: CoinUnloginContentProps) => {
  const isAds = rewardType === 'ADS-JOLI_COIN';
  const { t } = useTranslation();
  const coinUrl = CoinImageMap[rewardType];

  return (
    <div style={{ paddingTop: '80px' }}>
      <section
        style={`${styles.modal.styles} width:calc(100% - 40px); position: relative; border-radius: 16px; padding: 0;`}
      >
        <FixedImage src={coinUrl} alt="🪙" width={213} height={125} top={'-80px'} />
        <div style={styles.header.styles}>
          <button
            style={`${styles.closeButton.styles} padding-top: 10px; padding-right: 10px;`}
            onClick={onClose.bind(null, { type: 'CANCEL' })}
          >
            <Close />
          </button>
        </div>
        <div style={styles.introduct.styles}>
          {isAds ? <span style={styles.introductSubTitle.styles}>{t('loginGuide.instead')}</span> : null}
          <span style={styles.introductTitle.styles}>
            <Trans
              children={t('loginGuide.introduct')}
              values={{
                coin: (
                  <img
                    src={coinUrl}
                    alt="🪙"
                    style={{ height: '20px', verticalAlign: 'text-top', marginRight: '2px' }}
                  />
                )
              }}
            />
          </span>
        </div>

        <div
          style={{
            width: 'calc(100% - 40px)',
            paddingBottom: '16px'
          }}
        >
          <section
            style={`${styles.buttonContainer.styles} flex-direction: column; margin-top: 48px; padding: 0 20px;`}
          >
            <div style={{ position: 'relative', display: 'inline-block', width: '100%', height: '100%' }}>
              <div style={{ position: 'absolute', top: '-32px', right: '-5px', width: 'auto', zIndex: 1 }}>
                <TipsBubble>
                  <Trans
                    style={{
                      fontSize: '12px',
                      color: '#FFFFFF',
                      fontWeight: 600,
                      lineHeight: '16px',
                      textAlign: 'center'
                    }}
                    children={t('loginGuide.tips')}
                    values={{
                      coin: (
                        <>
                          <img
                            src={coinUrl}
                            alt="🪙"
                            style={{ height: '16px', verticalAlign: 'text-top', marginRight: '2px' }}
                          />
                          {rewardValue}
                        </>
                      )
                    }}
                  />
                </TipsBubble>
              </div>

              <button
                style={`${styles.confirmButton.styles} ${styles.buttonFlex.styles} padding: 0; line-height: 24px; display: flex; align-items: center; justify-content: center; overflow: hidden;`}
                onClick={onConfirm}
              >
                <span style="display: flex; align-items: center; justify-content: center; width: 100%; word-break: break-word; height: 48px;">
                  {t(confirmButtonKey)}
                </span>
              </button>
            </div>

            <button
              style={`${styles.cancelButton.styles} ${styles.buttonFlex.styles} line-height: 24px;`}
              onClick={onClose.bind(null, { type: 'FAILED' })}
            >
              {t(cancelButtonKey)}
            </button>
          </section>
        </div>
      </section>
    </div>
  );
};
